import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { push } from 'react-router-redux';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { signOut } from '../../store/actions';

import { Link, Button } from 'shared/lib/SureDesignSystem';
import SubMenu from './SubMenu';

import Logo from '../Logo';

import styles from './TopNavBar.module.css';

const propTypes = {
  className: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  userFullName: PropTypes.string,
};

const defaultProps = {
  className: null,
};

class TopNavBar extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isMenuOpen: false,
      isMobile: !!(window.screen.width <= 1024),
      openSubMenuId: null,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.onMenuClose();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = debounce(() => {
    if (window.screen.width <= 1024) {
      this.setState({
        isMobile: true,
      });
    } else {
      this.setState({
        isMobile: false,
      });
    }
  }, 500);

  onToggleMenuButtonClick = () => {
    this.setState(({ isMenuOpen }) => ({
      isMenuOpen: !isMenuOpen,
    }));
  };

  onMenuClose = () => {
    this.setState({
      openSubMenuId: null,
      isMenuOpen: false,
    });
  };

  onSubMenuToggle = subMenuId => () => {
    this.setState(({ openSubMenuId }) => ({
      openSubMenuId: subMenuId === openSubMenuId ? null : subMenuId,
    }));
  };

  onSubMenuClose = () => {
    this.setState({
      openSubMenuId: null,
    });
  };

  render() {
    const { isMenuOpen, isMobile, openSubMenuId } = this.state;
    const { className, isLoggedIn, signOut, userFullName } = this.props;
    return (
      <div className={classnames(styles.wrapper, className)}>
        <div className={styles.container}>
          {openSubMenuId ? (
            <button
              id="backButton"
              className={classnames([styles.menuButton, styles.backButton])}
              onClick={this.onSubMenuClose}
            >
              <i className="fas fa-chevron-left" /> Back
            </button>
          ) : (
            <button
              className={styles.menuButton}
              onClick={this.onToggleMenuButtonClick}
            >
              <i
                className={classnames('fas', {
                  'fa-bars': !isMenuOpen,
                  'fa-times': isMenuOpen,
                })}
              />
            </button>
          )}

          <Link to={isLoggedIn ? '/account/policies' : '/'}>
            <Logo className={styles.logo} />
          </Link>

          {isLoggedIn ? (
            <div
              className={classnames(
                styles.linksWrapper,
                styles.linksWrapperLoggedIn,
                {
                  [styles.isOpen]: isMenuOpen,
                }
              )}
            >
              <Link className={styles.link} to="/account/policies">
                Your Policies
              </Link>
              <a
                className={styles.link}
                href="https://support.sureapp.com/support/home"
                rel="noopener noreferrer"
                target="_blank"
              >
                Support
              </a>
              <Link className={styles.link} to="/claims">
                Claims
              </Link>
              <SubMenu
                isMobile={isMobile}
                isOpen={openSubMenuId === 'insurance'}
                isButton
                className={styles.buttonLink}
                onClose={this.onMenuClose}
                onToggle={this.onSubMenuToggle('insurance')}
                onBack={this.onSubMenuClose}
                groups={[
                  {
                    title: 'Personal',
                    items: [
                      {
                        title: 'Renters',
                        href: `https://www.${
                          process.env.REACT_APP_AMTRUST_DOMAIN
                        }.com/product/amtrust/amtrust-renters-direct/get-started`,
                      },
                      { title: 'Home Warranty', href: '/home-warranty' },
                    ],
                  },
                  {
                    title: 'Business',
                    items: [
                      {
                        title: 'Small Business',
                        href: '/small-business-owners-insurance',
                      },
                    ],
                  },
                  {
                    title: 'Travel',
                    items: [
                      { title: 'Baggage', href: '/baggage-protection' },
                      {
                        title: 'Rental Car',
                        href: '/rental-car-insurance',
                      },
                    ],
                  },
                ]}
              >
                <Button data-active={openSubMenuId === 'insurance'}>
                  Get Protection
                </Button>
              </SubMenu>
              <SubMenu
                isMobile={isMobile}
                isOpen={openSubMenuId === 'user'}
                isProfile
                className={styles.link}
                onClose={this.onMenuClose}
                onToggle={this.onSubMenuToggle('user')}
                onBack={this.onSubMenuClose}
                items={[
                  { title: 'Account Settings', href: '/account/settings' },
                  {
                    title: 'Sign Out',
                    onClick: signOut,
                  },
                ]}
              >
                {userFullName}
              </SubMenu>
            </div>
          ) : (
            <div
              className={classnames(styles.linksWrapper, {
                [styles.isOpen]: isMenuOpen,
              })}
            >
              <SubMenu
                isMobile={isMobile}
                isOpen={openSubMenuId === 'insurance'}
                className={styles.link}
                onClose={this.onMenuClose}
                onToggle={this.onSubMenuToggle('insurance')}
                onBack={this.onSubMenuClose}
                groups={[
                  {
                    title: 'Personal',
                    items: [
                      {
                        title: 'Renters',
                        href: `https://www.${
                          process.env.REACT_APP_AMTRUST_DOMAIN
                        }.com/product/amtrust/amtrust-renters-direct/get-started`,
                      },
                      {
                        title: 'Home Warranty',
                        href: '/home-warranty',
                      },
                    ],
                  },
                  {
                    title: 'Business',
                    items: [
                      {
                        title: 'Small Business',
                        href: '/small-business-owners-insurance',
                      },
                    ],
                  },
                  {
                    title: 'Travel',
                    items: [
                      { title: 'Baggage', href: '/baggage-protection' },
                      {
                        title: 'Rental Car',
                        href: '/rental-car-insurance',
                      },
                    ],
                  },
                ]}
              >
                Insurance
              </SubMenu>
              <Link
                className={styles.link}
                to="/claims"
                onClick={this.onToggleMenuButtonClick}
              >
                Claims
              </Link>

              <Link
                className={styles.link}
                to="/about"
                onClick={this.onToggleMenuButtonClick}
              >
                Company
              </Link>

              <Link
                className={classnames(styles.link, styles.phone)}
                icon="fas fa-phone"
                href="tel:(844) 335-5441"
              >
                (844) 335-5441
              </Link>

              <Link
                className={styles.signUpLink}
                href="https://support.sureapp.com/support/home"
                rel="noopener noreferrer"
                target="_blank"
              >
                Support
              </Link>
              <div className={classnames(styles.surePlatform)}>
                <Link href="https://www.sureapp.com">
                  <Button>Visit Sure Platform</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    isLoggedIn: !!state.authToken,
    userFullName: `${get(state, 'account.firstName')} ${get(
      state,
      'account.lastName'
    )}`,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    signOut: () => {
      dispatch(signOut());
      dispatch(push('/'));
    },
    navTo: path => dispatch(push(path)),
  };
};

const enhance = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
);

TopNavBar.propTypes = propTypes;
TopNavBar.defaultProps = defaultProps;

export default enhance(TopNavBar);
